/**
 * Record a receipt — money in, by any of the six manual modes (spec §G4,
 * ADR-0011 §6). Split out of `Receipts.tsx` and laid out as the Branches
 * dialog in Wave 15D (C14 §10.6 item 7; T-6.16). Behaviour unchanged.
 *
 * **One form for all six manual modes**, because that is the requirement and not
 * a shortcut: a cheque, a cash payment, an NEFT and a gateway capture produce
 * the same row, credit the same ledger and publish the same event. A gateway
 * receipt is **not** entered here (C10, ADR-0027 §6): it is written by the
 * capture when the money arrives, and a hand-typed one would carry no payment
 * id for a refund to go back through. The only thing the mode changes here is
 * the label above the reference box — "Cheque number" against "UTR" against
 * "UPI reference" — because that is the one place a person needs to be told
 * which number to type.
 *
 * **Application is oldest-first unless the firm directs it** (FR-G4). The form
 * offers the client's open invoices with what each still owes; leaving them all
 * alone applies the receipt from the oldest, which is what a firm does by
 * default and what its clients expect.
 */

import { Button, Dialog, Input, Select, Textarea } from '@fiveox/ui'
import { useEffect, useState } from 'react'

import { FORM_GRID, FormRow } from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  type Invoice,
  MANUAL_RECEIPT_MODES,
  MODE_LABELS,
  type ManualReceiptMode,
  type Receipt,
  billing,
  iso,
  rupees,
} from '../../lib/api/billing'
import { type ClientRecord, clients } from '../../lib/api/clients'
import { CLIENT_PAGE, DialogFooter } from './shared'

/** What the reference box is called, per mode. The one thing that varies. */
export const REFERENCE_LABEL: Record<ManualReceiptMode, string> = {
  cheque: 'Cheque number',
  cash: 'Reference (optional)',
  bank_transfer: 'Transaction reference',
  neft: 'UTR',
  rtgs: 'UTR',
  upi: 'UPI reference',
}

export function RecordReceiptDialog({
  onClose,
  onRecorded,
}: {
  onClose: () => void
  onRecorded: (receipt: Receipt) => void
}) {
  const [book, setBook] = useState<ClientRecord[]>([])
  const [open, setOpen] = useState<Invoice[]>([])
  const [client, setClient] = useState('')
  const [mode, setMode] = useState<ManualReceiptMode>('cheque')
  const [amount, setAmount] = useState('')
  const [received, setReceived] = useState(iso(new Date()))
  const [reference, setReference] = useState('')
  const [bank, setBank] = useState('')
  const [notes, setNotes] = useState('')
  const [directed, setDirected] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void clients
      .list({ status: 'active', limit: CLIENT_PAGE })
      .then((r) => setBook(r.data))
      .catch(() => setError('The clients could not be loaded.'))
  }, [])

  useEffect(() => {
    setDirected({})
    if (!client) {
      setOpen([])
      return
    }
    void billing
      .invoices({ client_id: client, unpaid: true, limit: 100 })
      .then((r) => setOpen(r.data))
      .catch(() => setOpen([]))
  }, [client])

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      const allocations = Object.entries(directed)
        .filter(([, value]) => value.trim() !== '')
        .map(([invoice_id, value]) => ({ invoice_id, amount: value.trim() }))
      const resp = await billing.recordReceipt({
        client_id: client,
        mode,
        amount: amount.trim(),
        received_on: received,
        reference: reference.trim() || null,
        bank_name: bank.trim() || null,
        notes: notes.trim() || null,
        // Undefined, not an empty array: `[]` means *"direct it nowhere"* and
        // would hold the whole receipt as an advance, where the firm meant
        // "apply it the usual way".
        allocations: allocations.length ? allocations : undefined,
      })
      onRecorded(resp.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The receipt could not be recorded.')
      setSaving(false)
    }
  }

  const why = !client
    ? 'Choose the client who paid.'
    : !amount.trim()
      ? 'Type the amount received.'
      : undefined

  return (
    // The kit's dialog: bounded by the window, the body scrolls, and the
    // failure is pinned under the title rather than scrolled away with it.
    <Dialog open onClose={onClose} title="Record a receipt" error={error} width="lg">
      <div className={FORM_GRID}>
        <FormRow label="Client" htmlFor="receipt-client">
          <Select id="receipt-client" value={client} onChange={(e) => setClient(e.target.value)}>
            <option value="">Choose a client…</option>
            {book.map((c) => (
              <option key={c.id} value={c.id}>
                {c.legal_name}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="Mode" htmlFor="receipt-mode">
          <Select
            id="receipt-mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as ManualReceiptMode)}
          >
            {MANUAL_RECEIPT_MODES.map((m) => (
              <option key={m} value={m}>
                {MODE_LABELS[m]}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="Amount" htmlFor="receipt-amount">
          <Input
            id="receipt-amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
          />
        </FormRow>
        <FormRow label="Received on" htmlFor="receipt-received">
          <Input
            id="receipt-received"
            type="date"
            value={received}
            onChange={(e) => setReceived(e.target.value)}
          />
        </FormRow>
        <FormRow label={REFERENCE_LABEL[mode]} htmlFor="receipt-reference">
          <Input
            id="receipt-reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </FormRow>
        <FormRow label="Bank" htmlFor="receipt-bank">
          <Input id="receipt-bank" value={bank} onChange={(e) => setBank(e.target.value)} />
        </FormRow>
        <FormRow label="Notes" htmlFor="receipt-notes">
          <Textarea
            id="receipt-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </FormRow>
      </div>

      {open.length > 0 && (
        <fieldset className="border-border mt-6 space-y-3 border-t pt-5">
          <legend className="text-foreground text-sm font-semibold">Where it goes</legend>
          <p className="text-muted-foreground text-xs">
            Leave these empty and the receipt is applied to the oldest invoice first. Anything left
            over is held as an advance.
          </p>
          {open.map((invoice) => (
            <div
              key={invoice.id}
              className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_8rem] sm:items-center"
            >
              <div className="min-w-0 text-sm">
                <span className="font-medium">{invoice.number}</span>
                <span className="text-muted-foreground">
                  {' '}
                  · {rupees(invoice.outstanding)} outstanding
                </span>
              </div>
              <Input
                aria-label={`Amount for ${invoice.number ?? 'this invoice'}`}
                value={directed[invoice.id] ?? ''}
                onChange={(e) =>
                  setDirected((current) => ({ ...current, [invoice.id]: e.target.value }))
                }
                placeholder="Amount"
                inputMode="decimal"
              />
            </div>
          ))}
        </fieldset>
      )}

      <DialogFooter onCancel={onClose} busy={saving}>
        <span title={why}>
          <Button onClick={() => void submit()} disabled={why !== undefined} loading={saving}>
            Record
          </Button>
        </span>
      </DialogFooter>
    </Dialog>
  )
}
